import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit3,
  CheckCircle2,
  XCircle,
  Building2,
} from "lucide-react";
import {
  listCorporates,
  activateCorporate,
  deactivateCorporate,
} from "../../../Services/adminCorporates";
import StatusBadge from "../../../Components/admin/shared/StatusBadge";
import EmptyState from "../../../Components/admin/shared/EmptyState";
import ConfirmDialog from "../../../Components/admin/shared/ConfirmDialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Card, CardContent } from "../../../ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../ui/dropdown-menu";
import { Skeleton } from "../../../ui/skeleton";

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "inactive", label: "Inactive" },
];

const PAGE_SIZE = 20;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return "—";
  }
};

const SeatCount = ({ used, limit }) => {
  if (limit == null || limit === 0) {
    return <span>{used ?? 0} / unlimited</span>;
  }
  return (
    <span>
      {used ?? 0} / {limit}
    </span>
  );
};

const AdminCorporatesList = () => {
  const navigate = useNavigate();
  const [corporates, setCorporates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // { corp, action: "activate" | "deactivate" }

  // Debounce search by 300ms (per spec).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCorporates({
        page,
        page_size: PAGE_SIZE,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: debouncedSearch || undefined,
      });
      setCorporates(data?.corporates || []);
      setTotalPages(data?.total_pages || 1);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load enterprises");
      setCorporates([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = (next) => {
    setStatusFilter(next);
    setPage(1);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { corp, action } = pendingAction;
    if (action === "activate") {
      await activateCorporate(corp.id);
    } else if (action === "deactivate") {
      await deactivateCorporate(corp.id);
    }
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Enterprise</h1>
          <p className="text-sm text-muted-foreground">
            Manage enterprise sponsors, branding, members, and access.
          </p>
        </div>
        <Button onClick={() => navigate("/admin/enterprises/new")}>
          <Plus className="mr-2 h-4 w-4" /> New enterprise
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search by name, slug, or domain…"
                className="pl-9"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : corporates.length === 0 ? (
            <EmptyState
              icon={<Building2 size={40} />}
              title="No enterprises yet"
              description={
                debouncedSearch || statusFilter !== "all"
                  ? "No enterprises match your current filters."
                  : "Create your first enterprise to get started."
              }
              action={{
                label: "New enterprise",
                onClick: () => navigate("/admin/enterprises/new"),
              }}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Seats</TableHead>
                    <TableHead>Contract end</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {corporates.map((corp) => (
                    <TableRow key={corp.id}>
                      <TableCell className="font-medium">
                        {corp.display_name || corp.name}
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/enterprises/${corp.id}`)}
                          className="border-0 bg-transparent p-0 text-primary hover:underline cursor-pointer"
                        >
                          {corp.slug}
                        </button>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={corp.status} />
                      </TableCell>
                      <TableCell>
                        <SeatCount
                          used={corp.seats_used}
                          limit={corp.contract_seat_limit ?? corp.seat_limit}
                        />
                      </TableCell>
                      <TableCell>{formatDate(corp.contract_end_date)}</TableCell>
                      <TableCell>{formatDate(corp.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/admin/enterprises/${corp.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(`/admin/enterprises/${corp.id}?edit=1`)
                              }
                            >
                              <Edit3 className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            {corp.status === "active" ? (
                              <DropdownMenuItem
                                onSelect={(e) => {
                                  e.preventDefault();
                                  setPendingAction({ corp, action: "deactivate" });
                                }}
                              >
                                <XCircle className="mr-2 h-4 w-4" /> Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onSelect={(e) => {
                                  e.preventDefault();
                                  setPendingAction({ corp, action: "activate" });
                                }}
                              >
                                <CheckCircle2 className="mr-2 h-4 w-4" /> Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!pendingAction}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        title={
          pendingAction?.action === "activate"
            ? `Activate ${pendingAction?.corp?.display_name || pendingAction?.corp?.name}?`
            : `Deactivate ${pendingAction?.corp?.display_name || pendingAction?.corp?.name}?`
        }
        description={
          pendingAction?.action === "activate"
            ? "Activated enterprises appear publicly at their slug and can accept new sign-ups."
            : "Deactivating hides the public activation page and blocks new sign-ups. Existing members keep access."
        }
        confirmLabel={pendingAction?.action === "activate" ? "Activate" : "Deactivate"}
        destructive={pendingAction?.action === "deactivate"}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
};

export default AdminCorporatesList;
